/// A simplified synoptic chart — a depression with attached warm
/// + cold fronts, isobars round the low, and a high pressure to the
/// south-east. Pedagogical, not real Met-Office data.

interface Props {
  size?: number;
}

const VW = 480;
const VH = 300;
const MUTED = "var(--text-muted, #94a3b8)";
const LOW = "#c0382b";
const HIGH = "#1e6091";
const WARM = "#f5b400";
const COLD = "#1e6091";

export function SynopticChartFigure({ size = VW }: Props) {
  const low = { x: 160, y: 120 };
  const high = { x: 400, y: 220 };

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Synoptic chart — depression with warm and cold fronts"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
      />

      {/* Isobars round the low — concentric ellipses. */}
      {[1, 1.6, 2.4, 3.4].map((r, i) => (
        <ellipse
          key={`l-${i}`}
          cx={low.x}
          cy={low.y}
          rx={r * 32}
          ry={r * 26}
          fill="none"
          stroke={MUTED}
          strokeWidth={i === 0 ? 1.2 : 0.7}
        />
      ))}

      {/* Isobars round the high. */}
      {[1, 1.6, 2.4].map((r, i) => (
        <circle
          key={`h-${i}`}
          cx={high.x}
          cy={high.y}
          r={r * 30}
          fill="none"
          stroke={MUTED}
          strokeWidth={i === 0 ? 1.2 : 0.7}
        />
      ))}

      {/* Pressure labels. */}
      <text x={low.x} y={low.y + 5} fontSize={28} fontWeight={700} fill={LOW} textAnchor="middle">
        L
      </text>
      <text x={low.x} y={low.y - 50} fontSize={11} fontWeight={600} fill={LOW} textAnchor="middle">
        992 mb
      </text>
      <text x={high.x} y={high.y + 5} fontSize={28} fontWeight={700} fill={HIGH} textAnchor="middle">
        H
      </text>
      <text x={high.x} y={high.y - 40} fontSize={11} fontWeight={600} fill={HIGH} textAnchor="middle">
        1032 mb
      </text>

      {/* Warm front trailing south-east from the low. */}
      <path
        d={`M ${low.x + 20} ${low.y + 30} Q ${low.x + 80} ${low.y + 80} ${low.x + 160} ${low.y + 120}`}
        fill="none"
        stroke={WARM}
        strokeWidth={2.5}
      />
      <WarmSymbols
        startX={low.x + 30}
        startY={low.y + 38}
        endX={low.x + 160}
        endY={low.y + 120}
      />

      {/* Cold front trailing south-west from the low. */}
      <path
        d={`M ${low.x + 20} ${low.y + 30} Q ${low.x - 30} ${low.y + 80} ${low.x - 80} ${low.y + 140}`}
        fill="none"
        stroke={COLD}
        strokeWidth={2.5}
      />
      <ColdSymbols
        startX={low.x + 10}
        startY={low.y + 40}
        endX={low.x - 80}
        endY={low.y + 140}
      />

      {/* Wind arrows in the warm sector (between the two fronts) —
          shows the south-westerly flow typical of a UK warm sector. */}
      <WindBarb x={low.x + 60} y={low.y + 90} deg={45} />
      <WindBarb x={low.x + 30} y={low.y + 130} deg={45} />

      {/* Wind arrows on the eastern side of the high (light SE-erly). */}
      <WindBarb x={high.x - 40} y={high.y - 50} deg={315} />

      {/* North arrow + scale. */}
      <g transform={`translate(${VW - 60} 30)`}>
        <line x1={0} y1={0} x2={0} y2={-25} stroke="#0f172a" strokeWidth={2} />
        <polygon points={`-4,-22 0,-30 4,-22`} fill="#0f172a" />
        <text x={0} y={10} fontSize={11} fontWeight={700} textAnchor="middle">
          N
        </text>
      </g>

      {/* Caption. */}
      <text x={10} y={VH - 8} fontSize={10} fill={MUTED}>
        Atlantic depression L 992 mb with warm + cold fronts; Azores high H 1032 mb to the SE.
      </text>
    </svg>
  );
}

function WarmSymbols({
  startX,
  startY,
  endX,
  endY,
}: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) {
  // Place semicircles along the front pointing in the direction of motion.
  const ANG = Math.atan2(endY - startY, endX - startX);
  const perpX = -Math.sin(ANG);
  const perpY = Math.cos(ANG);
  const out: React.ReactNode[] = [];
  const steps = 5;
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) / steps;
    const x = startX + (endX - startX) * t;
    const y = startY + (endY - startY) * t;
    const cx = x - perpX * 6;
    const cy = y - perpY * 6;
    out.push(<circle key={i} cx={cx} cy={cy} r={4} fill={WARM} />);
  }
  return <g>{out}</g>;
}

function ColdSymbols({
  startX,
  startY,
  endX,
  endY,
}: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) {
  const ANG = Math.atan2(endY - startY, endX - startX);
  const perpX = -Math.sin(ANG);
  const perpY = Math.cos(ANG);
  const out: React.ReactNode[] = [];
  const steps = 5;
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) / steps;
    const x = startX + (endX - startX) * t;
    const y = startY + (endY - startY) * t;
    const apex = { x: x - perpX * 8, y: y - perpY * 8 };
    const base1 = { x: x - perpX * 1, y: y - perpY * 1 };
    const base2 = {
      x: base1.x + Math.cos(ANG) * 5,
      y: base1.y + Math.sin(ANG) * 5,
    };
    out.push(
      <polygon
        key={i}
        points={`${apex.x},${apex.y} ${base1.x},${base1.y} ${base2.x},${base2.y}`}
        fill={COLD}
      />,
    );
  }
  return <g>{out}</g>;
}

function WindBarb({
  x,
  y,
  deg,
}: {
  x: number;
  y: number;
  deg: number;
}) {
  const rad = (deg * Math.PI) / 180;
  const len = 24;
  const ex = x + Math.cos(rad) * len;
  const ey = y + Math.sin(rad) * len;
  return (
    <g>
      <line x1={x} y1={y} x2={ex} y2={ey} stroke="#475569" strokeWidth={1.3} />
      <polygon
        points={`${ex},${ey} ${ex - 5 * Math.cos(rad - 0.4)},${ey - 5 * Math.sin(rad - 0.4)} ${ex - 5 * Math.cos(rad + 0.4)},${ey - 5 * Math.sin(rad + 0.4)}`}
        fill="#475569"
      />
    </g>
  );
}
